import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import {
    networkErrors,
    serverErrors,
    responseErrors,
    parsingErrors,
    flowErrors,
} from './errors.js';

const tokensStorageKey = 'authTokens';

async function storeString(key, value) {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (error) {
        console.log('Error at web/local-storage/main::storeString');
        console.log(error);
        return false;
    }
}

async function storeObject(key, value) {
    try {
        const json = JSON.stringify(value);
        return await storeString(key, json);
    } catch (error) {
        console.log('Error at web/local-storage/main::storeObject');
        console.log(error);
        return false;
    }
}

async function getString(key) {
    try {
        const value = localStorage.getItem(key);
        return value;
    } catch (error) {
        console.log('Error at web/local-storage/main::getString');
        console.log(error);
        return null;
    }
}

async function getObject(key) {
    try {
        const json = await getString(key);
        if (json === null) return null;

        return JSON.parse(json);
    } catch (error) {
        console.log('Error at web/local-storage/main::getObject');
        console.log(error);
        return null;
    }
}

class APIClient {
    #appType = null;
    #logger = null;
    #apiBaseUrl = null;
    #axiosInstance = null;
    #requestMethods = null;
    #apiEndpoints = null;
    #authTokens = null;
    #isLoadingAuthTokensFromStorage = true;
    #customerRideMethods = null;
    #driverRideMethods = null;
    #refreshAccessTokenConsecutiveFailureCount = 0;
    #refreshAccessTokenConsecutiveFailureLogoutThreshold = 3;
    //
    #interactionStateFetchRequestInProgress = false;

    constructor(apiBaseUrl, appType, logger) {
        this.#appType = appType;
        this.#logger = logger;

        this.#apiBaseUrl = apiBaseUrl;
        this.#axiosInstance = axios.create({
            baseURL: apiBaseUrl,
            timeout: 10_000,
            // validateStatus: () => true,
        });

        this.#requestMethods = {
            GET: this.#axiosInstance.get,
            POST: this.#axiosInstance.post,
            PUT: this.#axiosInstance.put,
            PATCH: this.#axiosInstance.patch,
            DELETE: this.#axiosInstance.delete,
        };

        this.#apiEndpoints = {
            data: {
                localities: '/data/localities',
            },
            auth: {
                requestLoginOtp: '/auth/request-login-otp',
                requestSignupOtp: '/auth/signup/customer/request-otp',
                login: '/auth/login',
                signup: '/auth/signup/customer/verify-otp-and-create-account',
                refreshAccessToken: '/auth/refresh-access-token',
                blacklistRefreshToken: '/auth/blacklist-refresh-token',
            },
            core: {
                userStatus: '/core/user-status',
                userInteractionState: '/core/user-interaction-state',
                latestCompletedRide: '/core/latest-completed-ride',
                userLocation: '/core/user-location',
                userDeviceFcmToken: '/core/user-device-fcm-token',
            },
            rides: {
                customer: {
                    numIdleDrivers: '/rides/customer/num-idle-drivers',
                    initializeRideBooking: '/rides/customer/initialize-booking',
                    cancelRideBooking: '/rides/customer/cancel-booking',
                    queueRideBooking: '/rides/customer/queue-booking',
                    queuedBookingStatus: '/rides/customer/queued-booking-status',
                    rideBookingLogicData: '/rides/customer/ride-booking-logic-data',
                },
                driver: {
                    startRide: '/rides/driver/start-ride',
                    markCustomerDroppedOff: '/rides/driver/mark-customer-dropped-off',
                },
            },
            analytics: {
                registerDeviceDetailsEvent: '/analytics/device/details',
            },
        };

        this.#customerRideMethods = {
            getNumIdleDrivers: this.#getNumIdleDrivers.bind(this),
            initializeRideBooking: this.#initializeRideBooking.bind(this),
            cancelRideBooking: this.#cancelRideBooking.bind(this),
            queueRideBooking: this.#queueRideBooking.bind(this),
            getQueuedBookingStatus: this.#getQueuedBookingStatus.bind(this),
            getRideBookingLogicData: this.#getRideBookingLogicData.bind(this),
        };

        this.#driverRideMethods = {
            startRide: this.#startRide.bind(this),
            markCustomerDroppedOff: this.#markCustomerDroppedOff.bind(this),
        };

        this.methods = {
            data: {
                localities: this.#getLocalities.bind(this),
            },
            auth: {
                requestLoginOtp: this.#requestLoginOtp.bind(this),
                requestSignupOtp: this.#requestSignupOtp.bind(this),
                login: this.#login.bind(this),
                signup: this.#signup.bind(this),
                blacklistRefreshToken: this.#blacklistRefreshToken.bind(this),
            },
            core: {
                patchUserStatus: this.#patchUserStatus.bind(this),
                getUserInteractionState: this.#getUserInteractionState.bind(this),
                getLatestCompletedRide: this.#getLatestCompletedRide.bind(this),
                patchUserCurrentLocation: this.#patchUserCurrentLocation.bind(this),
                patchUserDeviceFcmToken: this.#patchUserDeviceFcmToken.bind(this),
            },
            analytics: {
                registerDeviceDetailsEvent: this.registerDeviceDetailsEvent.bind(this),
            },
        };

        if (this.#appType === 'CUSTOMER') {
            this.methods = {
                ...this.methods,
                rides: {
                    customer: this.#customerRideMethods,
                },
            };
        } else if (this.#appType === 'DRIVER') {
            this.methods = {
                ...this.methods,
                rides: {
                    driver: this.#driverRideMethods,
                },
            };
        }

        this.#isLoadingAuthTokensFromStorage = true;
        this.#loadAuthTokensFromStorage();
    }

    // Initialization Utilities
    async #loadAuthTokensFromStorage() {
        let loadedAuthTokens = await getObject(tokensStorageKey);
        if (loadedAuthTokens === null) {
            loadedAuthTokens = {
                access: null,
                refresh: null,
            };
        }
        this.#authTokens = loadedAuthTokens;

        this.#refreshAccessTokenConsecutiveFailureCount = 0;
        this.#isLoadingAuthTokensFromStorage = false;
    }

    isLoggedIn() {
        const value = localStorage.getItem(tokensStorageKey);
        return value !== null;
    }

    //////////

    // Request Utilities
    async #setAndSaveAuthTokensToLocalStorage(accessToken = null, refreshToken = null) {
        let updatedAuthTokens = this.#authTokens;

        if (accessToken !== null) {
            updatedAuthTokens.access = accessToken;
        }

        if (refreshToken !== null) {
            updatedAuthTokens.refresh = refreshToken;
        }

        this.#authTokens = updatedAuthTokens;
        await storeObject(tokensStorageKey, updatedAuthTokens);
    }

    #isAccessTokenExpired(token_expiration_margin = 5_000) {
        const accessToken = this.#authTokens.access;

        if (!accessToken) {
            return true;
        }

        try {
            const accessTokenPayload = jwtDecode(accessToken);
            if (!accessTokenPayload || !accessTokenPayload.exp) {
                return true;
            }

            const expirationTimestamp = accessTokenPayload.exp * 1_000;

            return Date.now() >= expirationTimestamp - token_expiration_margin;
        } catch (error) {
            this.#logger.error('Failed to decode accessToken', {
                error: error,
                accessToken: accessToken,
                parentClass: 'APIClient',
                functionName: 'isAccessTokenExpired',
            });
            return true;
        }
    }

    #parseError(error, requestUrl) {
        let requestId = null;

        if (error?.response?.headers) {
            requestId = error.response.headers['x-zoto-request-id'];
        }

        const errorCode = error.code;
        if (errorCode in networkErrors) {
            this.#logger.error('Network error for request', {
                requestUrl: requestUrl,
                errorCode: errorCode,
                parentClass: 'APIClient',
                functionName: 'parseError',
            });
            return networkErrors[errorCode];
        }

        if (error.status === 500) {
            this.#logger.error('Internal Server Error (500) for request', {
                requestUrl: requestUrl,
                parentClass: 'APIClient',
                functionName: 'parseError',
                requestId: requestId,
            });

            return serverErrors.internal_server_error;
        }

        if (!error?.response?.data?.error?.code) {
            this.#logger.error('Error but no response error code for request', {
                requestUrl: requestUrl,
                parentClass: 'APIClient',
                functionName: 'parseError',
                requestId: requestId,
            });
            return serverErrors.internal_server_error;
        }

        const errorResponseCode = error.response?.data?.error?.code;

        if (error.response?.data?.error?.userFriendlyError) {
            this.#logger.error('Response error for request', {
                requestUrl: requestUrl,
                errorResponseCode: errorResponseCode,
                parentClass: 'APIClient',
                functionName: 'parseError',
                requestId: requestId,
            });
            return error.response.data.error;
        }

        if (errorResponseCode in responseErrors) {
            this.#logger.error('Response error for request', {
                requestUrl: requestUrl,
                errorResponseCode: errorResponseCode,
                parentClass: 'APIClient',
                functionName: 'parseError',
                requestId: requestId,
            });
            return responseErrors[errorResponseCode];
        }

        this.#logger.error('Response error (unknown) for request', {
            requestUrl: requestUrl,
            errorResponseCode: errorResponseCode,
            parentClass: 'APIClient',
            functionName: 'parseError',
            requestId: requestId,
        });
        return responseErrors.unknown_error;
    }

    async #refreshAccessToken() {
        const requestBody = {
            refreshToken: this.#authTokens.refresh,
        };

        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.refreshAccessToken,
            requestBody
        );

        if (!responseData.success) {
            this.#refreshAccessTokenConsecutiveFailureCount += 1;

            if (
                this.#refreshAccessTokenConsecutiveFailureCount >
                this.#refreshAccessTokenConsecutiveFailureLogoutThreshold
            ) {
                this.#logger.error('Repeated failure to refresh accessToken', {
                    refreshAccessTokenConsecutiveFailureCount:
                        this.#refreshAccessTokenConsecutiveFailureCount,
                    refreshAccessTokenConsecutiveFailureLogoutThreshold:
                        this.#refreshAccessTokenConsecutiveFailureLogoutThreshold,
                    parentClass: 'APIClient',
                    functionName: 'makeRequest',
                });
                return {
                    success: false,
                    error: flowErrors.refresh_access_token_repeated_failure,
                };
            }

            this.#logger.error('Failed to refresh accessToken', {
                refreshAccessTokenConsecutiveFailureCount:
                    this.#refreshAccessTokenConsecutiveFailureCount,
                refreshAccessTokenConsecutiveFailureLogoutThreshold:
                    this.#refreshAccessTokenConsecutiveFailureLogoutThreshold,
                parentClass: 'APIClient',
                functionName: 'makeRequest',
            });
            return {
                success: false,
                error: flowErrors.refresh_access_token_failure,
            };
        }

        await this.#setAndSaveAuthTokensToLocalStorage(responseData.result.tokens.access, null);
        this.#refreshAccessTokenConsecutiveFailureCount = 0;
        return responseData;
    }

    async #makeRequest(method, url, body = {}, headers = {}, addAuth = false) {
        if (addAuth) {
            if (!this.#authTokens.access || !this.#authTokens.refresh) {
                this.#logger.error(
                    'authTokens are not loaded but attempted to make authenticated request',
                    {
                        requestUrl: url,
                        parentClass: 'APIClient',
                        functionName: 'makeRequest',
                    }
                );

                return {
                    success: false,
                    error: flowErrors.not_logged_in,
                };
            }

            if (this.#isAccessTokenExpired()) {
                const oldAccessToken = this.#authTokens.access;

                const refreshAccessTokenResponseData = await this.#refreshAccessToken();
                if (!refreshAccessTokenResponseData.success) {
                    return refreshAccessTokenResponseData;
                }

                const x = this.#isAccessTokenExpired();
                if (x) {
                }

                const newAccessToken = this.#authTokens.access;
            } else {
            }

            const accessToken = this.#authTokens.access;
            headers.Authorization = `Bearer ${accessToken}`;
        }

        const requestConfig = {
            headers: headers,
        };

        let response = null;

        try {
            if (method === 'GET') {
                response = await this.#requestMethods[method](url, requestConfig);
            } else {
                response = await this.#requestMethods[method](url, body, requestConfig);
            }
        } catch (error) {
            try {
                const parsedError = this.#parseError(error, url);
                return {
                    success: false,
                    error: parsedError,
                };
            } catch (error2) {
                console.log(error2);
                return {
                    success: false,
                    error: {
                        code: 'parse_error_failure',
                        userFriendlyError: {
                            title: 'Application Error',
                            detail: 'An error occurred within the app.',
                        },
                    },
                };
            }
        }

        const responseContentType = response.headers['content-type'];
        if (responseContentType && responseContentType.includes('application/json')) {
            const responseData = response.data;
            return {
                success: true,
                ...responseData,
            };
        }

        this.#logger.error('Received non-json response for request', {
            requestUrl: url,
            parentClass: 'APIClient',
            functionName: 'makeRequest',
        });

        return {
            success: false,
            error: parsingErrors.non_json_response_received,
        };
    }
    //////////

    // Data APIs
    async #getLocalities() {
        const responseData = await this.#makeRequest('GET', this.#apiEndpoints.data.localities);
        return responseData;
    }
    //////////

    // Auth APIs
    async #requestLoginOtp(phoneNumber) {
        const requestBody = {
            phoneNumber: phoneNumber,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.requestLoginOtp,
            requestBody
        );
        return responseData;
    }

    async #requestSignupOtp(phoneNumber) {
        const requestBody = {
            phoneNumber: phoneNumber,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.requestSignupOtp,
            requestBody
        );
        return responseData;
    }

    async #login(phoneNumber, enteredOtp, deviceFcmToken = 'null') {
        const requestBody = {
            phoneNumber: phoneNumber,
            enteredOtp: enteredOtp,
            deviceFcmToken: deviceFcmToken,
            appType: this.#appType,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.login,
            requestBody
        );

        if (responseData.success) {
            await this.#setAndSaveAuthTokensToLocalStorage(
                responseData.result.tokens.access,
                responseData.result.tokens.refresh
            );
            this.#refreshAccessTokenConsecutiveFailureCount = 0;
        }

        return responseData;
    }

    async #signup(enteredOtp, fullName, locality, phoneNumber) {
        const requestBody = {
            enteredOtp: enteredOtp,
            fullName: fullName,
            locality: locality,
            phoneNumber: phoneNumber,
            appType: this.appType,
        };

        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.signup,
            requestBody
        );

        if (responseData.success) {
            await this.#setAndSaveAuthTokensToLocalStorage(
                responseData.result.tokens.access,
                responseData.result.tokens.refresh
            );
            this.#refreshAccessTokenConsecutiveFailureCount = 0;
        }

        return responseData;
    }

    async #blacklistRefreshToken(refreshToken) {
        const requestBody = {
            refreshToken: refreshToken,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.auth.blacklistRefreshToken,
            requestBody
        );
        return responseData;
    }
    //////////

    // Core APIs
    async #patchUserStatus(newUserStatus) {
        const requestBody = {
            newUserStatus: newUserStatus,
        };
        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.core.userStatus,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    async #getUserInteractionState() {
        console.log('Fetching user interaction state');

        const isLoggedInResult = this.isLoggedIn();
        if (!isLoggedInResult) {
            console.log('User not logged in, cannot fetch interaction state');

            return {
                success: false,
                error: {
                    code: 'not_logged_in',
                },
            };
        }

        if (this.#interactionStateFetchRequestInProgress) {
            console.log('Interaction state fetch already in progress, skipping duplicate request');

            return {
                success: false,
                error: {
                    code: 'interaction_state_fetch_already_in_progress',
                },
            };
        }

        this.#interactionStateFetchRequestInProgress = true;

        let responseData = null;
        try {
            responseData = await this.#makeRequest(
                'GET',
                this.#apiEndpoints.core.userInteractionState,
                {},
                {},
                true
            );
        } catch (error) {
            this.#interactionStateFetchRequestInProgress = false;
            responseData = null;
        } finally {
            this.#interactionStateFetchRequestInProgress = false;
        }

        if (responseData === null) {
            console.log('Failed to fetch interaction state due to unknown error');

            return {
                success: false,
                error: {
                    code: 'interaction_state_fetch_failure',
                },
            };
        }

        return responseData;
    }

    async #getLatestCompletedRide() {
        const responseData = await this.#makeRequest(
            'GET',
            this.#apiEndpoints.core.latestCompletedRide,
            {},
            {},
            true
        );
        return responseData;
    }

    async #patchUserCurrentLocation(deviceCurrentLocation) {
        const requestBody = {
            currentLocation: deviceCurrentLocation,
        };
        const responseData = await this.#makeRequest(
            'GET',
            this.#apiEndpoints.core.userInteractionState,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    async #patchUserDeviceFcmToken(deviceFcmToken) {
        const requestBody = {
            deviceFcmToken: deviceFcmToken,
        };
        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.core.userDeviceFcmToken,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    //////////

    // Customer APIs
    async #getNumIdleDrivers() {
        const responseData = await this.#makeRequest(
            'GET',
            this.#apiEndpoints.rides.customer.numIdleDrivers,
            {},
            {},
            true
        );
        return responseData;
    }

    async #getRideBookingLogicData() {
        const responseData = await this.#makeRequest(
            'GET',
            this.#apiEndpoints.rides.customer.rideBookingLogicData,
            {},
            {},
            true
        );
        return responseData;
    }

    async #queueRideBooking(currentLocation) {
        const requestBody = {
            currentLocation: currentLocation,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.rides.customer.queueRideBooking,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    async #getQueuedBookingStatus() {
        const responseData = await this.#makeRequest(
            'GET',
            this.#apiEndpoints.rides.customer.queuedBookingStatus,
            {},
            {},
            true
        );
        return responseData;
    }

    async #initializeRideBooking(currentLocation) {
        const requestBody = {
            currentLocation: currentLocation,
        };
        const responseData = await this.#makeRequest(
            'POST',
            this.#apiEndpoints.rides.customer.initializeRideBooking,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    async #cancelRideBooking(cancellationReason) {
        const requestBody = {
            cancellationReason: cancellationReason,
        };

        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.rides.customer.cancelRideBooking,
            requestBody,
            {},
            true
        );
        return responseData;
    }
    //////////

    // Driver APIs
    async #startRide(enteredOtp, currentLocation = null) {
        const requestBody = {
            enteredOtp: enteredOtp,
        };

        if (currentLocation !== null) {
            requestBody.currentLocation = currentLocation;
        }

        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.rides.driver.startRide,
            requestBody,
            {},
            true
        );
        return responseData;
    }

    async #markCustomerDroppedOff(currentLocation = null) {
        const requestBody = {};
        if (currentLocation !== null) {
            requestBody.currentLocation = currentLocation;
        }

        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.rides.driver.markCustomerDroppedOff,
            requestBody,
            {},
            true
        );
        return responseData;
    }
    //////////

    // Analytics APIs
    async registerDeviceDetailsEvent(deviceDetails) {
        const requestBody = {
            deviceDetails: deviceDetails,
        };

        const responseData = await this.#makeRequest(
            'PATCH',
            this.#apiEndpoints.analytics.registerDeviceDetailsEvent,
            requestBody,
            {},
            true
        );
        return responseData;
    }
}

function createApiClient(apiBaseUrl, appType, logger) {
    const apiClient = new APIClient(apiBaseUrl, appType, logger);
    return apiClient;
}

class Logger {
    debug(message, meta = {}) {
        console.debug(message, meta);
    }

    error(message, meta = {}) {
        console.error(message, meta);
    }
}
const defaultLogger = new Logger();

const defaultApiClient = createApiClient('https://api.zipzyride.com', 'CUSTOMER', defaultLogger);
export {
    createApiClient,
    defaultApiClient,
    storeObject,
    getObject,
    storeString,
    getString,
    defaultLogger,
};
